import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { ZoomIn, ZoomOut, Check, X } from 'lucide-react';

const createCroppedImage = (imageSrc, pixelCrop) => {
  return new Promise((resolve) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(
        image,
        pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height,
        0, 0, pixelCrop.width, pixelCrop.height
      );
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', 0.9);
    };
    image.src = imageSrc;
  });
};

const ImageCropper = ({ imageSrc, onCropDone, onCancel, aspectRatio = 1, cropShape = 'round' }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleDone = async () => {
    if (!croppedAreaPixels) return;
    const blob = await createCroppedImage(imageSrc, croppedAreaPixels);
    onCropDone(blob);
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-[60] flex flex-col" data-testid="image-cropper-modal">
      <div className="relative flex-1">
        <Cropper
          image={imageSrc}
          crop={crop}
          zoom={zoom}
          aspect={aspectRatio}
          cropShape={cropShape}
          onCropChange={setCrop}
          onCropComplete={onCropComplete}
          onZoomChange={setZoom}
        />
      </div>
      <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <ZoomOut className="w-4 h-4 text-slate-400" />
          <input
            type="range"
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="w-32 accent-blue-500"
            data-testid="crop-zoom-slider"
          />
          <ZoomIn className="w-4 h-4 text-slate-400" />
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onCancel}
            className="h-10 px-5 rounded-full bg-slate-700 text-white font-medium hover:bg-slate-600 transition-all flex items-center gap-2"
            data-testid="crop-cancel-btn"
          >
            <X className="w-4 h-4" /> Cancel
          </button>
          <button
            onClick={handleDone}
            className="h-10 px-5 rounded-full bg-blue-600 text-white font-medium hover:bg-blue-700 transition-all flex items-center gap-2"
            data-testid="crop-done-btn"
          >
            <Check className="w-4 h-4" /> Crop & Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default ImageCropper;
